// UnitApplicationPermissions
// ***********************************************************************************************************************
import { Box, FormControlLabel, Grid, Radio, Typography } from "@mui/material";
import * as React from "react";
import { Dropdown, IDropdownAction } from "ui/Scripts/source/components/framework/Dropdown/Dropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { EnumHelper, GroupType, UserGroupType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { ICompanyListItem } from "../../../../models/company/companyListItem";
import { IUnitNode, UnitNode } from "../../../../models/unit/unitNode";
import { IUnitNodePermission } from "../../../../models/unit/unitNodePermission";
import { IUnitPermissionUserGroup, UnitPermissionUserGroup } from "../../../../models/userGroup/unitPermissionUserGroup";
import { UnitPermissionTree } from "../../../unit/Unit/UnitPermissionTree";
import { ActionContainer } from "../../ActionContainer";
import { GridRow } from "../../Grid/GridRow";
import { ListIconButtonsPermission } from "../../List/ListIconButtons/ListIconButtonsPermission";
import { ListHeaderLine } from "../../List/ListLine";
import { UnitGroupPermissionDialog } from "../UnitGroupPermissionDialog";
import { AppUtils } from "source/framework/appUtils";
import { VerticalContainer } from "../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/VerticalContainer";

export interface IUnitApplicationPermissionsProp {
    classes?: string;
    companyId: string;
    unitId: string;
    disabled?: boolean;
    groups: IUnitPermissionUserGroup[];
    hideAllUsers?: boolean;
    hideUserGroups?: boolean;
    unitNode: IUnitNode;
    accessRightNoneTitle: string;
    accessRightReadTitle: string;
    accessRightWriteTitle: string;
    additionalPermissionTitle: string[];
    validAdditionalPermissions: number[];
    partnerCompaniesTitle?: string;
    partnerCompanies?: ICompanyListItem[];
    onChangeSaveUnitNode: (saveUnitNode: IUnitNode) => void;
    cardView?: boolean;
}

interface IUnitApplicationPermissionsState {
    additionalPermissionInUse: boolean[];
    groups: IUnitPermissionUserGroup[];
    allUsersGroup: boolean;
    userGroupType: UserGroupType;
    userGroups: IUnitPermissionUserGroup[];
    userGroupDropDownItems: IDropdownAction[];
    userGroup: IUnitPermissionUserGroup;
    copiedPermissions: { [id: string]: IUnitNodePermission; };
    permissionsCopied: boolean;
    unitNode: IUnitNode;
    displayUnitNode: IUnitNode;
    editUnitNode: IUnitNode;
    selectedUnitId: string;
    showUnitGroupPermissionDialog: boolean;
    unitGroupPermissionDialogUserGroup: IUnitPermissionUserGroup;
}

export class UnitApplicationPermissions extends React.Component<IUnitApplicationPermissionsProp, IUnitApplicationPermissionsState> {
    setFixedGroupNames = (groups: IUnitPermissionUserGroup[]) => {
        const fixedGroups = groups.filter(i => !!i.code && Base.equalInt(i.type, UserGroupType.Fixed));
        for (const group of fixedGroups) {
            group.groupNames = groups.filter(i => Base.equalInt(i.type, UserGroupType.Normal) && group.groupCodes.indexOf(i.code) > -1).map(i => i.name).sort();
        }
    };

    getUserGroupDropDownItems = (userGroups: IUnitPermissionUserGroup[], selectedUserGroup: IUnitPermissionUserGroup): IDropdownAction[] => {
        return userGroups.map((userGroup) => {
            const groupNames = Base.getStringWithSeparators(userGroup.groupNames, ", ");
            return {
                title: <Box display="flex" alignItems="baseline"><Typography>{userGroup.name}</Typography>{groupNames && <Typography variant="caption" color="text.secondary">&nbsp;({groupNames})</Typography>}</Box>,
                textTitle: userGroup.name + (groupNames ? " (" + groupNames + ")" : ""),
                selected: selectedUserGroup.code === userGroup.code,
                onClick: () => this.userGroupChange(userGroup.code)
            };
        });
    };

    getUserGroupDropDownItemsAll = (userGroups: IUnitPermissionUserGroup[], selectedUserGroup: IUnitPermissionUserGroup, allUsersGroup: boolean, userGroupType: UserGroupType): IDropdownAction[] => {
        const nonQrCodeUserGroups = userGroups.filter(i => !i.qrCode);
        const qrCodeUserGroups = userGroups.filter(i => i.qrCode);
        const result = this.getUserGroupDropDownItems(nonQrCodeUserGroups, selectedUserGroup);
        if (nonQrCodeUserGroups.length > 0 && qrCodeUserGroups.length > 0 && !allUsersGroup) {
            const dividerTitle = userGroupType === UserGroupType.Normal
                ? Translations.QrCodeUserGroups
                : Translations.QrCodeUsers;
            result.push({
                title: dividerTitle,
                textTitle: dividerTitle,
                divider: true,
                onClick: null
            });
        }
        return result.concat(this.getUserGroupDropDownItems(qrCodeUserGroups, selectedUserGroup));
    };

    static getUserGroups = (groups: IUnitPermissionUserGroup[], allUsersGroup: boolean, userGroupType: UserGroupType): IUnitPermissionUserGroup[] => {
        return allUsersGroup
            ? groups.filter(i => !i.code && Base.equalInt(i.type, UserGroupType.Normal))
            : groups.filter(i => !!i.code && Base.equalInt(i.type, userGroupType));
    };

    getUserGroupsNew = (groups: IUnitPermissionUserGroup[], allUsersGroup: boolean, userGroupType: UserGroupType): {
        userGroups: IUnitPermissionUserGroup[];
        userGroup: IUnitPermissionUserGroup;
        userGroupDropDownItems: IDropdownAction[];
     } => {
        const userGroups = UnitApplicationPermissions.getUserGroups(groups, allUsersGroup, userGroupType);
        const userGroup = userGroups.length > 0 ? userGroups[0] : new UnitPermissionUserGroup();
        return {
            userGroups,
            userGroup,
            userGroupDropDownItems: this.getUserGroupDropDownItemsAll(userGroups, userGroup, allUsersGroup, userGroupType)
        };
    };

    constructor(props: IUnitApplicationPermissionsProp) {
        super(props);
        const groups = this.props.groups.map(i => new UnitPermissionUserGroup(i));
        this.setFixedGroupNames(groups);
        const userGroups = this.getUserGroupsNew(groups, !props.hideAllUsers, !props.hideUserGroups ? UserGroupType.Normal : UserGroupType.Fixed);
        const unitNode = new UnitNode(props.unitNode);
        unitNode.setEffectivePermissions(null, userGroups.userGroup);
        this.state = {
            additionalPermissionInUse: [
                !!props.additionalPermissionTitle[0] && unitNode.maxPermissionLevelA > 0,
                !!props.additionalPermissionTitle[1] && unitNode.maxPermissionLevelB > 0,
                !!props.additionalPermissionTitle[2] && unitNode.maxPermissionLevelC > 0
            ],
            groups,
            unitNode,
            displayUnitNode: unitNode.getUnitNodeById(props.unitId),
            editUnitNode: null,
            allUsersGroup: true,
            userGroupType: UserGroupType.Normal,
            userGroups: userGroups.userGroups,
            userGroup: userGroups.userGroup,
            userGroupDropDownItems: userGroups.userGroupDropDownItems,
            permissionsCopied: false,
            copiedPermissions: {},
            selectedUnitId: props.unitId,
            showUnitGroupPermissionDialog: false,
            unitGroupPermissionDialogUserGroup: null
        };
    }

    getUpdatedUnitNode = (userGroup: IUnitPermissionUserGroup): IUnitNode => {
        if (!userGroup) return this.state.unitNode;
        return AppUtils.runLongTask(() => {
            const unitNode = new UnitNode(this.state.unitNode);
            unitNode.setEffectivePermissions(null, userGroup);
            return unitNode;
        });
    };

    userGroupChange = (userGroupCode: string) => {
        const props = this.props;
        const userGroup = this.state.userGroups.find(i => i.code === userGroupCode);
        if (!userGroup) return;
        const unitNode = this.getUpdatedUnitNode(userGroup);
        this.setState({
            userGroup,
            userGroupDropDownItems: this.getUserGroupDropDownItemsAll(this.state.userGroups, userGroup, this.state.allUsersGroup, this.state.userGroupType),
            unitNode,
            displayUnitNode: unitNode.getUnitNodeById(props.unitId),
        });
    };

    handleChangeUserGroupTypeAll = (checked: boolean) => {
        if (!checked) return;
        const props = this.props;
        const userGroups = this.getUserGroupsNew(this.state.groups, true, UserGroupType.Normal);
        const unitNode = this.getUpdatedUnitNode(userGroups.userGroup);
        this.setState({
            allUsersGroup: true,
            userGroups: userGroups.userGroups,
            userGroup: userGroups.userGroup,
            userGroupDropDownItems: userGroups.userGroupDropDownItems,
            unitNode,
            displayUnitNode: unitNode.getUnitNodeById(props.unitId),
        });
    };

    handleChangeUserGroupType = (checked: boolean, userGroupType: UserGroupType) => {
        if (!checked) return;
        const props = this.props;
        const userGroups = this.getUserGroupsNew(this.state.groups, false, userGroupType);
        const unitNode = this.getUpdatedUnitNode(userGroups.userGroup);
        this.setState({
            allUsersGroup: false,
            userGroupType,
            userGroups: userGroups.userGroups,
            userGroup: userGroups.userGroup,
            userGroupDropDownItems: userGroups.userGroupDropDownItems,
            unitNode,
            displayUnitNode: unitNode.getUnitNodeById(props.unitId),
        });
    };
    
    editPermission = (id: string) => {
        const state = this.state;
        if (!id) return;
        const userGroup = state.userGroups.find(i => i.code === state.userGroup.code);
        if (!userGroup) return;
        const editUnitNode = state.unitNode.getUnitNodeById(id);
        if (!editUnitNode) return;
        this.setState({
            showUnitGroupPermissionDialog: true,
            editUnitNode,
            unitGroupPermissionDialogUserGroup: userGroup
        });
    };

    handleUnitNodeClick = (id: string) => {
        if (!id) return;
        this.setState({
            selectedUnitId: id
        });
    };

    handleEditPermission = (id: string) => {
        this.editPermission(id);
    };

    copyPermissionToSiblings = (unitNode: UnitNode, companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup, permission: number, additionalPermissionA: number, additionalPermissionB: number, additionalPermissionC: number) => {
        if (!unitNode.hasSiblings()) return;
        const siblings = unitNode.getSiblings();
        for (let i = 0; i < siblings.length; i++) {
            siblings[i].setGroupPermission(companyGroup, userGroup, permission, additionalPermissionA, additionalPermissionB, additionalPermissionC, false);
        }
    };

    getAdditionalPermissionIsDisabled = (permission: number, additionalPermissionIndex: number) :boolean => {
        const props = this.props;
        const index = permission * 3 + additionalPermissionIndex;
        if (props.validAdditionalPermissions.length <= index) return true;
        return props.validAdditionalPermissions[index] < 1;
    };

    getAdditionalPermissionInUse = (permission: number, additionalPermissionIndex: number): boolean => {
        const state = this.state;
        return state.additionalPermissionInUse[additionalPermissionIndex] && !this.getAdditionalPermissionIsDisabled(permission, additionalPermissionIndex);
    };

    handleUnitGroupPermissionDialogOk = (permission: number, additionalPermissionA: number, additionalPermissionB: number, additionalPermissionC: number, clearSubUnitPermissions: boolean, copyPermissionToAllSameLevelUnits: boolean, partnerIds: string[]) => {
        const obj = this;
        const state = this.state;
        const props = this.props;
        AppUtils.runLongTask(() => {
            const unitNode = new UnitNode(state.unitNode);
            const editUnitNode = unitNode.getUnitNodeById(state.editUnitNode.id);
            if (!editUnitNode) return;
            editUnitNode.setGroupPermission(null, state.unitGroupPermissionDialogUserGroup, permission, additionalPermissionA, additionalPermissionB, additionalPermissionC, clearSubUnitPermissions);
            editUnitNode.setGroupPermissionCompanyIds(state.unitGroupPermissionDialogUserGroup, partnerIds);
            let updateUnitNode = editUnitNode;
            if (copyPermissionToAllSameLevelUnits) {
                this.copyPermissionToSiblings(editUnitNode, null, state.unitGroupPermissionDialogUserGroup, permission, additionalPermissionA, additionalPermissionB, additionalPermissionC);
                updateUnitNode = editUnitNode.parent;
            }
            updateUnitNode.setEffectivePermissions(null, state.unitGroupPermissionDialogUserGroup);
            const displayUnitNode = unitNode.getUnitNodeById(props.unitId);
            obj.setState({
                showUnitGroupPermissionDialog: false,
                unitNode,
                displayUnitNode: unitNode.getUnitNodeById(props.unitId),
            });
            props.onChangeSaveUnitNode(displayUnitNode);
            return unitNode;
        });
    };

    handleUnitGroupPermissionDialogCancel = () => {
        this.setState({
            showUnitGroupPermissionDialog: false
        });
    };

    handleUnitGroupPermissionDialogRemove = () => {
        const state = this.state;
        const props = this.props;
        const unitNode = new UnitNode(state.unitNode);
        const editUnitNode = unitNode.getUnitNodeById(state.selectedUnitId);
        if (!editUnitNode) return;
        editUnitNode.removeGroupPermission(GroupType.UserGroup, state.unitGroupPermissionDialogUserGroup.code);
        editUnitNode.setEffectivePermissions(null, state.unitGroupPermissionDialogUserGroup);
        const displayUnitNode = unitNode.getUnitNodeById(props.unitId);
        this.setState({
            showUnitGroupPermissionDialog: false,
            unitNode,
            displayUnitNode,
        });
        props.onChangeSaveUnitNode(displayUnitNode);
    };

    handleCopyPermissions = () => {
        const state = this.state;
        const displayUnitNode = state.displayUnitNode;
        if (!displayUnitNode) return;
        const copiedPermissions: { [id: string]: IUnitNodePermission; } = {};
        this.setState({
            copiedPermissions: displayUnitNode.getNodePermissions(null, state.userGroup.code, copiedPermissions),
            permissionsCopied: true
        });
    };

    handlePastePermissions = () => {
        const obj = this;
        const props = this.props;
        const state = this.state;
        if (!state.permissionsCopied) return;
        const userGroup = state.userGroups.find(i => i.code === state.userGroup.code);
        if (!userGroup) return;
        const unitNode = new UnitNode(state.unitNode);
        const displayUnitNode = unitNode.getUnitNodeById(props.unitId);
        if (!displayUnitNode) return;
        return AppUtils.runLongTask(() => {
            const unitNode = new UnitNode(this.state.unitNode);
            displayUnitNode.setGroupPermissions(null, userGroup, state.copiedPermissions);
            displayUnitNode.setEffectivePermissions(null, userGroup);
            obj.setState({
                unitNode,
                displayUnitNode
            });
            props.onChangeSaveUnitNode(displayUnitNode);
            return unitNode;
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <VerticalContainer
                    before={<Box sx={{ margin: props.cardView ? "-1rem 1rem 2rem" : "-1rem 0 2rem" }}>
                        <GridRow sx={{ marginTop: "0" }}>
                            {!props.hideAllUsers &&
                                <Grid item sm={4}>
                                    <FormControlLabel
                                        label={Translations.UserGroupAll}
                                        control={
                                            <Radio
                                                checked={state.allUsersGroup}
                                                onChange={(_e, checked: boolean) => this.handleChangeUserGroupTypeAll(checked)}
                                            />
                                        }
                                    />
                                </Grid>
                            }
                            {!props.hideUserGroups &&
                                <Grid item sm={4}>
                                    <FormControlLabel
                                        label={Translations.UserGroups}
                                        control={
                                            <Radio
                                                checked={!state.allUsersGroup && EnumHelper.isEqual(state.userGroupType, UserGroupType.Normal)}
                                                onChange={(_e, checked: boolean) => this.handleChangeUserGroupType(checked, UserGroupType.Normal)}
                                            />
                                        }
                                    />
                                </Grid>
                            }
                            {(!props.hideAllUsers || !props.hideUserGroups) &&
                                <Grid item sm={4}>
                                    <FormControlLabel
                                        label={Translations.Users}
                                        control={
                                            <Radio
                                                checked={!state.allUsersGroup && EnumHelper.isEqual(state.userGroupType, UserGroupType.Fixed)}
                                                onChange={(_e, checked: boolean) => this.handleChangeUserGroupType(checked, UserGroupType.Fixed)}
                                            />
                                        }
                                    />
                                </Grid>
                            }
                        </GridRow>
                        <ActionContainer
                            leftItems={
                                <Dropdown
                                    selectedTitle={state.userGroup.name}
                                    selectMode={true}
                                    actions={state.userGroupDropDownItems}
                                />
                            }
                            rightItems={
                                <ListIconButtonsPermission
                                    editTooltip={Translations.EditPermission}
                                    copyTooltip={Translations.CopyGroupPermissions}
                                    pasteTooltip={Translations.PasteGroupPermissions}
                                    editDisabled={props.disabled}
                                    copyDisabled={props.disabled}
                                    pasteDisabled={props.disabled || !state.permissionsCopied}
                                    onEdit={() => this.handleEditPermission(state.selectedUnitId)}
                                    onCopy={this.handleCopyPermissions}
                                    onPaste={this.handlePastePermissions}
                                />
                            }
                        />
                    </Box>}
                    main={
                        <VerticalContainer
                            before={
                                <ListHeaderLine
                                    columns={[
                                        { size: 5, column: "name", content: Translations.Unit },
                                        { size: 7, column: "code", content: Translations.Permission }
                                    ]}
                                />
                            }
                            main={
                                <UnitPermissionTree
                                    unitNode={state.displayUnitNode}
                                    selectedId={state.selectedUnitId}
                                    isFirstNode={true}
                                    accessRightNoneTitle={props.accessRightNoneTitle}
                                    accessRightReadTitle={props.accessRightReadTitle}
                                    accessRightWriteTitle={props.accessRightWriteTitle}
                                    additionalPermissionTitle={props.additionalPermissionTitle}
                                    height="100%"
                                    getAdditionalPermissionInUse={this.getAdditionalPermissionInUse}
                                    onClick={this.handleUnitNodeClick}
                                    onOpen={this.handleEditPermission}
                                />
                            }
                            after={(props.partnerCompanies ?? []).length > 0
                                ? <Box display="flex" alignItems="center" sx={{ marginTop: ".5rem" }}>
                                    <Typography color="error" sx={{ margin: "0 .5rem" }}>*</Typography>
                                    <Typography>{Translations.UnitIsRelatedToEventPartner}</Typography>
                                </Box>
                                : null
                            }
                        />
                    }
                />
                {state.showUnitGroupPermissionDialog &&
                    <UnitGroupPermissionDialog
                        unitNode={state.editUnitNode}
                        companyGroup={null}
                        userGroup={state.unitGroupPermissionDialogUserGroup}
                        accessRightNoneTitle={props.accessRightNoneTitle}
                        accessRightReadTitle={props.accessRightReadTitle}
                        accessRightWriteTitle={props.accessRightWriteTitle}
                        additionalPermissionTitle={props.additionalPermissionTitle}
                        getAdditionalPermissionInUse={this.getAdditionalPermissionInUse}
                        partnerCompaniesTitle={props.partnerCompaniesTitle}
                        partnerCompanies={props.partnerCompanies}
                        onOk={this.handleUnitGroupPermissionDialogOk}
                        onRemove={this.handleUnitGroupPermissionDialogRemove}
                        onCancel={this.handleUnitGroupPermissionDialogCancel}
                    />
                }
            </>
        );
    }
}